/**
 * The weeks of a schedule period, with their true on-call ranges.
 *
 * Pure: takes dates and options, returns weeks. The rules are the owner's
 * (CLAUDE.md §11):
 *
 * - A week runs Monday to Sunday. The start snaps to the Monday on or before
 *   the chosen date.
 * - A Monday holiday makes the week BEFORE it the long-weekend week. That
 *   physician covers Monday through the holiday Monday (8 days), and the
 *   following week starts on Tuesday (6 days).
 * - A Friday holiday makes the week CONTAINING it the long-weekend week
 *   (7 days, no shift).
 * - Christmas week is the week containing Boxing Day; New Year week is the
 *   week after it. Special weeks keep Monday-to-Sunday boundaries and are
 *   never long weekends. An admin can override a week's special kind per
 *   schedule.
 *
 * Holidays at the edges of the period still count: a period that starts on a
 * holiday Monday starts on the Tuesday, and a period whose last week precedes
 * a holiday Monday runs 8 days past the chosen end date.
 */
import {
  addDays,
  addMonths,
  compareDates,
  daysBetween,
  mondayOnOrBefore,
  mondaysBetween,
  weekdayOnOrBefore,
  yearOf,
  type ISODate,
  SUNDAY,
} from '../dates/iso.ts';
import { DEFAULT_HOLIDAY_OPTIONS, ontarioHolidays, type Holiday, type HolidayOptions } from '../holidays/ontario.ts';

export type WeekKind = 'regular' | 'long-weekend' | 'christmas' | 'new-year';

/** What an admin can set a week to, overriding the automatic special-week rule. */
export type SpecialOverride = 'christmas' | 'new-year' | 'regular';

export interface HolidayMonday {
  date: ISODate;
  holiday: string;
}

export interface Week {
  /** The Monday that identifies the week. */
  weekStart: ISODate;
  /** First day the physician is on call: the Monday, or the Tuesday after a holiday Monday. */
  onCallStart: ISODate;
  /** Last day on call: the Sunday, or the holiday Monday for an 8-day week. */
  onCallEnd: ISODate;
  /** 6, 7 or 8. */
  days: number;
  kind: WeekKind;
  /** Names of the holidays that made this a long weekend (empty otherwise). */
  longWeekendHolidays: string[];
  /** The holiday Monday this week covers at its end, for an 8-day week. */
  coveredMonday: HolidayMonday | null;
  /** The holiday Monday this week does not include, for a 6-day week. */
  skippedMonday: HolidayMonday | null;
  /** True when an admin override, not the rule, decided `kind`. */
  overridden: boolean;
}

export interface BuildWeeksInput {
  /** Any date; snaps to the Monday on or before it. */
  start: ISODate;
  /** Inclusive. The last week is the one whose Monday is on or before this date. */
  end: ISODate;
  options?: HolidayOptions;
  /** Admin overrides keyed by the week's Monday. */
  overrides?: Readonly<Record<ISODate, SpecialOverride>>;
}

export const PERIOD_MONTHS = [3, 6, 9, 12] as const;
export type PeriodMonths = (typeof PERIOD_MONTHS)[number];
export const DEFAULT_PERIOD_MONTHS: PeriodMonths = 6;

/**
 * The Sunday that ends a period of whole weeks lasting about `months` from
 * a Monday start: the last Sunday on or before `start + months`.
 *
 * "On or before", not "before": when `start + months` is itself a Sunday,
 * that Sunday ends the period. This is what makes the group's half-years
 * come out as they really run, in 26-week halves: from Monday 4 January
 * 2027, six months later is Sunday 4 July 2027, the last day of the
 * January-to-July schedule. Stopping the day before would end it on Sunday
 * 27 June (25 weeks) and leave its last week, and any request for it,
 * outside the schedule. Twelve months never lands on a Sunday (a year moves
 * the weekday on by one or two days), so a year is always 52 weeks.
 *
 * Examples: 5 Jan 2026 → Sun 5 Jul 2026; 6 Jul 2026 → Sun 3 Jan 2027;
 * 8 Jun 2026 → Sun 6 Dec 2026 (all 26 weeks).
 */
export function periodEnd(start: ISODate, months: PeriodMonths): ISODate {
  const monday = mondayOnOrBefore(start);
  return weekdayOnOrBefore(addMonths(monday, months), SUNDAY);
}

/** Holidays for every year the period touches, plus a week of margin on each side. */
function holidaysAround(start: ISODate, end: ISODate, options: HolidayOptions): Holiday[] {
  const first = yearOf(addDays(start, -7));
  const last = yearOf(addDays(end, 14));
  const out: Holiday[] = [];
  for (let y = first; y <= last; y++) out.push(...ontarioHolidays(y, options));
  return out;
}

/** The Monday of the week containing Boxing Day, for each year in range. */
function christmasWeekStarts(start: ISODate, end: ISODate): ISODate[] {
  const out: ISODate[] = [];
  for (let y = yearOf(start) - 1; y <= yearOf(end); y++) out.push(mondayOnOrBefore(`${String(y)}-12-26`));
  return out;
}

export function buildWeeks(input: BuildWeeksInput): Week[] {
  const options = input.options ?? DEFAULT_HOLIDAY_OPTIONS;
  const overrides = input.overrides ?? {};
  const start = mondayOnOrBefore(input.start);
  if (compareDates(start, input.end) > 0) return [];
  const holidays = holidaysAround(start, input.end, options);
  const xmasStarts = new Set(christmasWeekStarts(start, input.end));
  const nyStarts = new Set([...xmasStarts].map((d) => addDays(d, 7)));

  // Which Mondays are long-weekend weeks, and why. Keyed by the week's Monday.
  const longWeekendBy = new Map<ISODate, string[]>();
  // Holiday Mondays: the week starting on one begins on the Tuesday.
  const holidayMondays = new Map<ISODate, string>();
  for (const h of holidays) {
    if (h.longWeekend === 'preceding-week') {
      const weekBefore = addDays(h.observed, -7);
      longWeekendBy.set(weekBefore, [...(longWeekendBy.get(weekBefore) ?? []), h.name]);
      holidayMondays.set(h.observed, h.name);
    } else if (h.longWeekend === 'containing-week') {
      const week = mondayOnOrBefore(h.observed);
      longWeekendBy.set(week, [...(longWeekendBy.get(week) ?? []), h.name]);
    }
  }

  return mondaysBetween(start, input.end).map((weekStart): Week => {
    const nextMonday = addDays(weekStart, 7);
    const coveredHoliday = holidayMondays.get(nextMonday);
    const skippedHoliday = holidayMondays.get(weekStart);
    const onCallStart = skippedHoliday ? addDays(weekStart, 1) : weekStart;
    const onCallEnd = coveredHoliday ? nextMonday : addDays(weekStart, 6);

    const automatic: WeekKind = xmasStarts.has(weekStart)
      ? 'christmas'
      : nyStarts.has(weekStart)
        ? 'new-year'
        : longWeekendBy.has(weekStart)
          ? 'long-weekend'
          : 'regular';
    const override = overrides[weekStart];
    let kind = automatic;
    if (override === 'christmas' || override === 'new-year') kind = override;
    else if (override === 'regular') kind = longWeekendBy.has(weekStart) ? 'long-weekend' : 'regular';
    const isSpecial = kind === 'christmas' || kind === 'new-year';

    return {
      weekStart,
      onCallStart,
      onCallEnd,
      days: daysBetween(onCallStart, onCallEnd) + 1,
      kind,
      // A special week is never counted as a long weekend, so it lists none.
      longWeekendHolidays: isSpecial ? [] : (longWeekendBy.get(weekStart) ?? []),
      coveredMonday: coveredHoliday ? { date: nextMonday, holiday: coveredHoliday } : null,
      skippedMonday: skippedHoliday ? { date: weekStart, holiday: skippedHoliday } : null,
      overridden: override !== undefined && kind !== automatic,
    };
  });
}
